import React, { useContext } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { DynamicComponent } from 'ctm-components';
import { ResponsiveLayout } from '@comparethemarketau/manor-layout';

import { createUseStyles } from 'react-jss';
import { ThemeContext } from 'styled-components';
import { PolicySummarySm } from './summary/policy_summary_sm';
import { PolicySummaryXs } from './summary/policy_summary_xs';
import { PolicySummaryLg } from './summary/policy_summary_lg';

import { ResultsPricesNoticeXs } from './disclaimers/results_prices_notice/results_prices_notice_xs';

import { HigherExcessDisclaimer } from './disclaimers/higher_excess/higher_excess_disclaimer';

import { PolicyFiltersSm } from './filters/policy_filters_sm';
import { PolicyFiltersXs } from './filters/policy_filters_xs';
import { PolicyFiltersLg } from './filters/policy_filters_lg';

import { PolicySortsSm } from './sorts/policy_sorts_sm';

import { PolicyProducts } from './products/policy_products';

import { UnavailableBrandsMd } from './unavailable_brands/unavailable_brands_md';
import { UnavailableBrandsSm } from './unavailable_brands/unavailable_brands_sm';
import { UnavailableBrandsXs } from './unavailable_brands/unavailable_brands_xs';

import { PageStyledWrapper } from './page_styled_wrapper';
import { withAvailableQuotes } from './products/common/with_available_quotes';

import { EmptyStateFullPage } from './empty_state/full_page/index';
import { EmptyStateConditional } from './empty_state/empty_state_conditional';

/**
 * Overwrirte global line-heigh to normal, global.css has body line-height 2.2rem
* */
const useStyles = createUseStyles((theme) => ({
    resultsPageDesign: {
        '& li': {
            lineHeight: 'normal'
        }
    }
}));

// eslint-disable-next-line valid-jsdoc
/**
 * Results Page
 *
 * Previous page render handler to see packages/ctm-components/components/results/comparable_results/comparable_results.js
 */
const _ResultsPageMain = ({ availableQuotesSize, firstOpen }) => {
    const enableFullEmptyPage = firstOpen && (availableQuotesSize === 0);
    const theme = useContext(ThemeContext);
    const classes = useStyles({ theme });

    return (
        <DynamicComponent
            component={(
                enableFullEmptyPage ? <EmptyStateFullPage />
                    : (
                        <PageStyledWrapper className={classNames('test-resultsPage-main', classes.resultsPageDesign)}>
                            <ResponsiveLayout is={'xs'}>
                                <PolicySummaryXs padding={['12', '16', '4', '16']} />
                                <ResultsPricesNoticeXs padding={['8', '16', '8', '16']} />
                                <HigherExcessDisclaimer padding={['8', '16', '8', '16']} />
                                <PolicyFiltersXs padding={['8', '16', '8', '16']} />
                                <PolicyProducts padding={['8', '16', '12', '16']} />
                                <UnavailableBrandsXs padding={['12', '16', '24', '16']} />
                                <EmptyStateConditional padding={['20', '32', '0', '32']} />
                            </ResponsiveLayout>
                            <ResponsiveLayout is={'sm'}>
                                <PolicySummarySm padding={['32', '32', '16', '32']} />
                                <PolicyFiltersSm padding={['16', '0', '12', '0']} />
                                <HigherExcessDisclaimer padding={['12', '32', '8', '32']} />
                                <PolicySortsSm padding={['8', '32', '8', '32']} />
                                <PolicyProducts padding={['8', '40', '0', '40']} />
                                <UnavailableBrandsSm padding={['0', '40', '12', '40']} />
                                <EmptyStateConditional padding={['44', '48', '0', '48']} />
                            </ResponsiveLayout>
                            <ResponsiveLayout is={'md'}>
                                <PolicySummarySm padding={['32', '32', '16', '32']} />
                                <PolicyFiltersSm padding={['16', '0', '12', '0']} />
                                <HigherExcessDisclaimer padding={['12', '32', '8', '32']} />
                                <PolicySortsSm padding={['8', '32', '8', '32']} />
                                <PolicyProducts padding={['8', '32', '0', '32']} />
                                <UnavailableBrandsMd padding={['8', '32', '12', '32']} />
                                <EmptyStateConditional padding={['44', '44', '0', '44']} />
                            </ResponsiveLayout>
                            <ResponsiveLayout isFrom={'lg'}>
                                <PolicySummaryLg padding={['32', '32', '16', '32']} />
                                <PolicyFiltersLg padding={['16', '0', '12', '0']} />
                                <HigherExcessDisclaimer padding={['12', '32', '12', '32']} />
                                <PolicySortsSm padding={['12', '32', '8', '32']} />
                                <PolicyProducts padding={['8', '32', '0', '32']} />
                                <UnavailableBrandsMd padding={['0', '32', '12', '32']} />
                                <EmptyStateConditional padding={['44', '0', '0', '0']} />
                            </ResponsiveLayout>
                        </PageStyledWrapper>
                    )
            )}
        />
    );
};

_ResultsPageMain.propTypes = {
    availableQuotesSize: PropTypes.number,
    firstOpen: PropTypes.bool
};

const mapping = (state) => {
    const lastViewedPage = state.getIn('page.lastViewedPage'.split('.'));
    const firstOpen = lastViewedPage !== 'results';
    return {
        firstOpen
    };
};

const ResultsPageMain = withAvailableQuotes(connect(mapping)(_ResultsPageMain));

export { ResultsPageMain };
